import {
  defineNuxtPlugin,
  useRuntimeConfig,
  useRouter,
  onBeforeUnmount,
} from '#imports'
import posthog from 'posthog-js'
import { useGdpr } from '~/composables/useGdpr'

export default defineNuxtPlugin(() => {
  // If in development mode, don't initialize PostHog at all
  if (import.meta.dev) {
    console.log('🚫 PostHog disabled in development mode')
    return
  }

  // Delay PostHog initialization until after hydration to prevent mismatches
  if (!import.meta.client) {
    return
  }

  const runtimeConfig = useRuntimeConfig()
  const { isEuCountry } = useGdpr()

  // Get API key with proper string type checking
  const apiKey = String(
    runtimeConfig.public.posthogPublicKey || process.env.POSTHOG_API_KEY || '',
  )

  // Get API host with proper string type checking
  const apiHost = String(
    runtimeConfig.public.posthogApiHost ||
      process.env.POSTHOG_API_HOST ||
      'https://eu.i.posthog.com',
  )

  // Get UI host with proper string type checking
  const uiHost = String(
    runtimeConfig.public.posthogUiHost ||
      process.env.POSTHOG_UI_HOST ||
      'https://eu.posthog.com',
  )

  // Generate a stable device ID that persists across page loads even without full consent
  // This ID won't be used for tracking until consent is given
  const getStableDeviceId = () => {
    if (typeof window === 'undefined') return ''

    let deviceId = localStorage.getItem('anonymous-device-id')
    if (!deviceId) {
      // Generate a proper UUID v4
      if (window.crypto && window.crypto.randomUUID) {
        // Modern browsers support randomUUID
        deviceId = window.crypto.randomUUID()
      } else {
        // Fallback for older browsers
        deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
          /[xy]/g,
          (c) => {
            const r = (Math.random() * 16) | 0
            const v = c === 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
          },
        )
      }
      localStorage.setItem('anonymous-device-id', deviceId)
    }
    return deviceId
  }

  // Set a consistent distinct_id for the user
  const deviceId = getStableDeviceId()

  // Initialize PostHog but don't start tracking until consent is given
  const posthogClient = posthog.init(apiKey, {
    api_host: apiHost,
    ui_host: uiHost,
    capture_pageview: 'history_change', // Only capture pageviews on router navigation
    capture_pageleave: false, // Disable automatic pageleave capture until opted in
    autocapture: false, // Disable autocapture until opted in
    persistence: 'localStorage', // Use localStorage consistently - consent is handled via opt_in/opt_out
    persistence_name: 'ph_aero', // Custom persistence name
    // GDPR compliance settings
    respect_dnt: true, // Respect Do Not Track
    // Note: IP anonymization is enabled by default in PostHog since October 2020
    property_blacklist: ['$ip'], // Ensure IP address is not captured
    loaded: (posthog) => {
      // Check the cookie preference in localStorage on load
      if (import.meta.client) {
        const cookiePreference = localStorage.getItem('cookie-preference')
        if (cookiePreference === 'accepted') {
          posthog.opt_in_capturing()
        } else if (cookiePreference === 'declined') {
          posthog.opt_out_capturing()
        } else {
          // For non-EU users, auto-opt-in
          if (!isEuCountry.value) {
            posthog.opt_in_capturing()
            localStorage.setItem('cookie-preference', 'accepted')
          } else {
            // Default to opted out for EU users until explicit consent
            posthog.opt_out_capturing()
          }
        }

        // Now that PostHog is fully loaded and consent is handled, set up router tracking
        const router = useRouter()

        // Helper function to convert path to absolute URL
        const getAbsoluteUrl = (path: string) => {
          if (typeof window === 'undefined') return path
          if (path.startsWith('http')) return path
          const baseUrl = window.location.origin
          const normalizedPath = path.startsWith('/') ? path : `/${path}`
          return `${baseUrl}${normalizedPath}`
        }

        // Handle pageLeave capture
        router.beforeEach((to, from) => {
          // Only capture pageLeave if not opted out and if not the initial navigation
          if (!posthog.has_opted_out_capturing() && from.name !== undefined) {
            posthog.capture('$pageleave', {
              $current_url: getAbsoluteUrl(from.fullPath),
            })
          }
        })

        // Store cleanup function
        const cleanup = () => {
          // Clean up any event listeners if needed
        }

        // Clean up on unmount
        onBeforeUnmount(cleanup)
      }
    },
  })

  // Set a consistent distinct_id for the user, even if they haven't given consent yet
  // This ensures the same user isn't counted multiple times on page reloads
  if (deviceId) {
    posthogClient.identify(deviceId, {}, {})
  }

  // Make PostHog available globally for the app
  return {
    provide: {
      clientPosthog: posthogClient,
    },
  }
})
